// src/components/PromemoriaNota.js
// «🔔 Ricordamelo» sulle note importanti (rosse) — W406. Regole e calcoli in
// utils/promemoriaRipetuti.js; l'invio lo fa il server ogni mezz'ora (functions).
// Il promemoria è personale (collezione «promemoria», regole 55): lo vede solo chi lo imposta.
import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { INTERVALLI, ORARI_FASCIA, GIORNI_SETTIMANA, DURATE, prossimoAvviso, fineDurata, testoRipeti, adessoChiave } from "../utils/promemoriaRipetuti";

const ETICHETTE_DURATA = { giorno: "per oggi", settimana: "per una settimana", mese: "per un mese", sempre: "finché la nota è rossa" };
const dataOra = (k) => (k ? `${k.slice(8, 10)}/${k.slice(5, 7)} alle ${k.slice(11, 16)}` : "");
const sel = { padding: "5px 7px", borderRadius: 7, border: "1px solid #fca5a5", background: "#fff", fontSize: 12.5, fontFamily: "'Inter',sans-serif" };

export default function PromemoriaNota({ nota }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const [mio, setMio] = useState(null);
  const [aperto, setAperto] = useState(false);
  const [tipo, setTipo] = useState("intervallo");
  const [minuti, setMinuti] = useState(60);
  const [ora, setOra] = useState("09:00");
  const [giorno, setGiorno] = useState(1);
  const [durata, setDurata] = useState("settimana");

  useEffect(() => {
    if (!uid || !nota?.id) return undefined;
    return onSnapshot(query(collection(db, "promemoria"), where("uid", "==", uid), where("rifId", "==", nota.id)),
      s => setMio(s.empty ? null : { id: s.docs[0].id, ...s.docs[0].data() }), () => setMio(null));
  }, [uid, nota?.id]);

  const apri = () => {
    const r = mio?.ripeti;
    if (r) { setTipo(r.tipo); if (r.minuti) setMinuti(r.minuti); if (r.ora) setOra(r.ora); if (r.giorno) setGiorno(r.giorno); setDurata(mio.durata || "settimana"); }
    setAperto(true);
  };
  const ripeti = tipo === "intervallo" ? { tipo, minuti: Number(minuti) } : tipo === "giorno" ? { tipo, ora } : { tipo, giorno: Number(giorno), ora };

  const salva = async () => {
    const adesso = adessoChiave();
    const fino = fineDurata(durata, adesso);
    const alle = prossimoAvviso(ripeti, adesso, fino);
    if (!alle) { addToast("Con questa scelta non resta nessun avviso: allunga la durata", "error"); return; }
    const dati = { alle, ripeti, durata, fino, titolo: String(nota.testo || "").slice(0, 80) || "Nota importante", projectId: nota.projectId || null, updatedAt: serverTimestamp() };
    try {
      if (mio) await updateDoc(doc(db, "promemoria", mio.id), dati);
      else await addDoc(collection(db, "promemoria"), { ...dati, uid, orgId: nota.orgId || null, rifCollezione: "noteAppunti", rifId: nota.id, createdAt: serverTimestamp() });
      addToast(`🔔 Te lo ricordo ${testoRipeti(ripeti)} — il primo il ${dataOra(alle)}`, "success");
      setAperto(false);
    } catch (e) { addToast("Promemoria non salvato: " + (e?.message || e), "error"); }
  };
  const togli = async () => {
    if (!mio) return;
    try { await deleteDoc(doc(db, "promemoria", mio.id)); addToast("Promemoria tolto", "info"); setAperto(false); }
    catch (e) { addToast("Promemoria non tolto: " + (e?.message || e), "error"); }
  };

  if (!uid || !nota?.id || nota.archiviata || nota.colore !== "rosso") return null;
  const btn = { padding: "4px 10px", borderRadius: 7, border: "1px solid #fca5a5", background: "#fff", color: "#b91c1c", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" };

  return (
    <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed #fca5a5" }}>
      {!aperto ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {mio ? <>
            <span style={{ fontSize: 12, color: "#991b1b", fontWeight: 600 }}>🔔 {testoRipeti(mio.ripeti)}, {ETICHETTE_DURATA[mio.durata] || ""} · prossimo il {dataOra(mio.alle)}</span>
            <button type="button" onClick={apri} style={btn}>Cambia</button>
            <button type="button" onClick={togli} style={{ ...btn, color: "#6b7280", borderColor: "#e5e7eb" }}>Togli</button>
          </> : <button type="button" onClick={apri} style={btn}>🔔 Ricordamelo…</button>}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 6, fontSize: 12.5, color: "#7f1d1d" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={sel}>
              <option value="intervallo">Ogni…</option><option value="giorno">Ogni giorno</option><option value="settimana">Ogni settimana</option>
            </select>
            {tipo === "intervallo" && (
              <select value={minuti} onChange={e => setMinuti(Number(e.target.value))} style={sel}>
                {INTERVALLI.map(m => <option key={m} value={m}>{m === 30 ? "30 minuti" : m === 60 ? "1 ora" : `${m / 60} ore`}</option>)}
              </select>
            )}
            {tipo === "settimana" && (
              <select value={giorno} onChange={e => setGiorno(Number(e.target.value))} style={sel}>
                {GIORNI_SETTIMANA.slice(1).map((g, i) => <option key={g} value={i + 1}>il {g}</option>)}
              </select>
            )}
            {tipo !== "intervallo" && (
              <select value={ora} onChange={e => setOra(e.target.value)} style={sel}>{ORARI_FASCIA.map(o => <option key={o} value={o}>alle {o}</option>)}</select>
            )}
            <select value={durata} onChange={e => setDurata(e.target.value)} style={sel}>
              {Object.keys(DURATE).map(d => <option key={d} value={d}>{ETICHETTE_DURATA[d]}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11.5, color: "#991b1b" }}>Solo dalle 7:00 alle 19:00, nella campanella e come notifica sul telefono (niente email). Si ferma da solo quando archivi la nota o le cambi colore.</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={salva} style={{ ...btn, background: "#dc2626", color: "#fff", borderColor: "#dc2626" }}>Salva</button>
            <button type="button" onClick={() => setAperto(false)} style={{ ...btn, color: "#6b7280", borderColor: "#e5e7eb" }}>Annulla</button>
            {mio && <button type="button" onClick={togli} style={{ ...btn, color: "#6b7280", borderColor: "#e5e7eb" }}>Togli il promemoria</button>}
          </div>
        </div>
      )}
    </div>
  );
}
